/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import {
  IconCamera,
  IconChevronDown,
  IconChevronRight,
  IconDashboard,
  IconFileAi,
  IconFileText, // Better for documents
  IconHelp,
  IconUserShield, // Better for user management
  IconSettings,
  IconAdjustmentsHorizontal, // Better for system config
  IconCategory, // Better for case categories
  IconCloudUpload, // Better for upload
  IconFiles, // Better for my documents
  IconArchive, // Better for all documents
  IconChartBar, // Better for reports
  IconBuildingBank,
  IconFlame,
  IconFileDescription,
  IconPhysotherapist, // Better for police station
} from "@tabler/icons-react";

import { NavDocuments } from "@/components/nav-documents";
import { NavMain } from "@/components/nav-main";
import { NavSecondary } from "@/components/nav-secondary";
import { NavUser } from "@/components/nav-user";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";
import { Link } from "react-router-dom";
import logo from "../assets/favicon.png";
import { getUserInfo } from "@/services/userService";
import { BarChart3, FileText, Shield, TrendingUp, Upload, Users } from "lucide-react";
import { useAppStore } from "@/store/appStore";

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const userInfo = getUserInfo();
  const { _filterOptions } = useAppStore(); 
  const user = userInfo ? userInfo : { role: null };
  const role = user ? user.role : null;
  const superAdminAccess = role === "ADMIN" || role === "SUPERADMIN" || role === "DM";
  const adminShoCoSdmDmAccess = role === "ADMIN" || role === "SUPERADMIN" || role === "SHO" || role === "CO" || role === "SDM" || role === "DM";
  const policeStations = user ? user.policeStations : [];
  
  const [isStationDropdownOpen, setIsStationDropdownOpen] = React.useState(false);
  const [isCaseDropdownOpen, setIsCaseDropdownOpen] = React.useState(false);
  const [isRiskDropdownOpen, setIsRiskDropdownOpen] = React.useState(false);
  const [isCriminalDropdownOpen, setIsCriminalDropdownOpen] = React.useState(false);
  const [isReportsDropdownOpen, setIsReportsDropdownOpen] = React.useState(false);

  const data = {
    user: {
      name: user ? user.name : "Abhishek",
      email: user? user.email : "abhigdrv@gmail.com",
      avatar: "/avatars/shadcn.jpg",
    },
    navMain: [
      {
        title: "Dashboard",
        url: "/",
        icon: IconDashboard,
        hasAccess: adminShoCoSdmDmAccess,
      },
      {
        title: "User Management",
        url: "/user-management",
        icon: IconUserShield, // Changed from IconUsers
        hasAccess: superAdminAccess,
      },
      {
        title: "System Config",
        url: "/system-config",
        icon: IconAdjustmentsHorizontal, // Changed from IconSettings2
        hasAccess: role === "SUPERADMIN",
      },
      {
        title: "Case Categories",
        url: "/case-categories",
        icon: IconCategory, // Changed from IconFileDescription
        hasAccess: superAdminAccess,
      },
    ],
    navClouds: [
      {
        title: "Capture",
        icon: IconCamera,
        isActive: true,
        url: "#",
        items: [
          {
            title: "Active Proposals",
            url: "#",
          },
          {
            title: "Archived",
            url: "#",
          },
        ],
      },
      {
        title: "Proposal",
        icon: IconFileText, // Changed from IconFileDescription
        url: "#",
        items: [
          {
            title: "Active Proposals",
            url: "#",
          },
          {
            title: "Archived",
            url: "#",
          },
        ],
      },
      {
        title: "Prompts",
        icon: IconFileAi,
        url: "#",
        items: [
          {
            title: "Active Proposals",
            url: "#",
          },
          {
            title: "Archived",
            url: "#",
          },
        ],
      },
    ],
    navSecondary: [
      {
        title: "Settings",
        url: "/settings",
        icon: IconSettings,
        hasAccess: adminShoCoSdmDmAccess,
      },
      {
        title: "Get Help",
        url: "/help",
        icon: IconHelp,
        hasAccess: adminShoCoSdmDmAccess,
      },
    ],
    documents: [
      {
        name: "Upload Document",
        url: "/upload-document",
        icon: IconCloudUpload, // Changed from IconDatabase
        hasAccess: adminShoCoSdmDmAccess,
      },
      {
        name: "My Documents",
        url: "/my-documents",
        icon: IconFiles, // Changed from IconFolder
        hasAccess: adminShoCoSdmDmAccess,
      },
      {
        name: "All PS Uploaded Docs",
        url: "/all-documents",
        icon: IconArchive, // Changed from IconListDetails
        hasAccess: superAdminAccess,
      },
    ],
    reports: [
      // {
      //   name: "All Reports",
      //   url: "/reports",
      //   icon: IconChartBar, // Changed from IconReport
      //   hasAccess: superAdminAccess,
      // },
      {
        name: "PS Wise Report",
        url: "/reports/ps-wise-report",
        icon: IconBuildingBank, // Changed from IconReport
        hasAccess: adminShoCoSdmDmAccess,
      },
      {
        name: "CrPC 107 / BNSS 126",
        url: "/reports/crpc-107",
        icon: IconFileText,
        hasAccess: adminShoCoSdmDmAccess,
      },
      {
        name: "CrPC 111 / BNSS 130",
        url: "/reports/crpc-111",
        icon: IconFileText,
        hasAccess: adminShoCoSdmDmAccess,
      },
      {
        name: "CrPC 117 / BNSS 136",
        url: "/reports/crpc-117",
        icon: IconFileText,
        hasAccess: adminShoCoSdmDmAccess,
      },
      {
        name: "CrPC 116(3) / BNSS 135(3)",
        url: "/reports/crpc-116-3",
        icon: IconFileText,
        hasAccess: adminShoCoSdmDmAccess,
      },
      {
        name: "GD Summary",
        url: "/reports/gd-summary",
        icon: FileText, // Changed from IconReport
        hasAccess: adminShoCoSdmDmAccess,
      },
      {
        name: "Case Type & Section Analysis",
        url: "/reports/case-analysis",
        icon: BarChart3, // Changed from IconReport
        hasAccess: adminShoCoSdmDmAccess,
      },
      {
        name: "Suspects & Victims",
        url: "/reports/suspects-victims",
        icon: Users, // Changed from IconReport
        hasAccess: adminShoCoSdmDmAccess,
      },
      {
        name: "Officer Wise GD Entry",
        url: "/reports/officer-wise",
        icon: Shield, // Changed from IconReport
        hasAccess: adminShoCoSdmDmAccess,
      },
      {
        name: "Incident Trends",
        url: "/reports/incident-trends",
        icon: TrendingUp, // Changed from IconReport
        hasAccess: superAdminAccess,
      },
    ],
  };

  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader className="rounded-md shadow-md">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild className="data-[slot=sidebar-menu-button]:!p-1.5">
              <Link to="/">
                <img src={logo} alt="Logo" width={28} />
                <span className="text-base font-semibold bg-gradient-to-r from-[#0d2846] to-[#e2af58] bg-clip-text text-transparent">Pabandi Portal</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={data.navMain} />
        <NavDocuments type="Documents" items={data.documents} />
        
        {/* Station Wise Document Dropdown */}
        <SidebarMenu className="mt-2">
          <SidebarMenuItem>
            <SidebarMenuButton 
              asChild 
              onClick={() => setIsStationDropdownOpen(!isStationDropdownOpen)} 
              className="cursor-pointer flex items-center justify-between"
            >
              <div className="flex items-center w-full">
                <IconBuildingBank className="mr-2 h-4 w-4" />
                <span>Station Wise Document</span>
                <span className="ml-auto">
                  {isStationDropdownOpen ? 
                    <IconChevronDown className="h-4 w-4" /> : 
                    <IconChevronRight className="h-4 w-4" />
                  }
                </span>
              </div>
            </SidebarMenuButton>
            {isStationDropdownOpen && (
              <div className="ml-8 mt-1 space-y-1">
                {policeStations.map((item: any, idx: number) => (
                  <Link 
                    key={idx} 
                    to={`/all-documents?policeStation=${item.name}`} 
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground hover:bg-sidebar-accent px-2 py-1 rounded-md"
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            )}
          </SidebarMenuItem>
        </SidebarMenu>

        {/* Category Wise Document Dropdown */}
        <SidebarMenu className="mt-2">
          <SidebarMenuItem>
            <SidebarMenuButton 
              asChild 
              onClick={() => setIsCaseDropdownOpen(!isCaseDropdownOpen)} 
              className="cursor-pointer flex items-center justify-between"
            >
              <div className="flex items-center w-full">
                <IconFileDescription className="mr-2 h-4 w-4" />
                <span>Category Wise Document</span>
                <span className="ml-auto">
                  {isCaseDropdownOpen ? 
                    <IconChevronDown className="h-4 w-4" /> : 
                    <IconChevronRight className="h-4 w-4" />
                  }
                </span>
              </div>
            </SidebarMenuButton>
            {isCaseDropdownOpen && (
              <div className="ml-8 mt-1 space-y-1">
                {_filterOptions?.caseCategories?.map((item: any, idx: number) => (
                  <Link 
                    key={'cc'+idx} 
                    to={`/all-documents?caseCategory=${item.title}`} 
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground hover:bg-sidebar-accent px-2 py-1 rounded-md"
                  >
                    {item.title}
                  </Link>
                ))}
              </div>
            )}
          </SidebarMenuItem>
        </SidebarMenu>

        {/* Risk Wise Document Dropdown */}
        <SidebarMenu className="mt-2">
          <SidebarMenuItem>
            <SidebarMenuButton 
              asChild 
              onClick={() => setIsRiskDropdownOpen(!isRiskDropdownOpen)} 
              className="cursor-pointer flex items-center justify-between"
            >
              <div className="flex items-center w-full">
                <IconFlame className="mr-2 h-4 w-4" />
                <span>Risk Wise Document</span>
                <span className="ml-auto">
                  {isRiskDropdownOpen ? 
                    <IconChevronDown className="h-4 w-4" /> : 
                    <IconChevronRight className="h-4 w-4" />
                  }
                </span>
              </div>
            </SidebarMenuButton>
            {isRiskDropdownOpen && (
              <div className="ml-8 mt-1 space-y-1">
                {[{title:'Low', value:'low'},{title:'Medium', value:'medium'},{title:'High', value:'high'}].map((item: any, idx: number) => (
                  <Link 
                    key={'cc'+idx} 
                    to={`/all-documents?riskLevel=${item.value}`} 
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground hover:bg-sidebar-accent px-2 py-1 rounded-md"
                  >
                    {item.title}
                  </Link>
                ))}
              </div>
            )}
          </SidebarMenuItem>
        </SidebarMenu>

        {/* Criminal Management */}
        <SidebarMenu className="mt-2">
          <SidebarMenuItem>
            <SidebarMenuButton 
              asChild 
              onClick={() => setIsCriminalDropdownOpen(!isCriminalDropdownOpen)} 
              className="cursor-pointer flex items-center justify-between"
            >
              <div className="flex items-center w-full">
                <IconPhysotherapist className="mr-2 h-4 w-4" />
                <span>Criminal Management</span>
                <span className="ml-auto">
                  {isCriminalDropdownOpen ? 
                    <IconChevronDown className="h-4 w-4" /> : 
                    <IconChevronRight className="h-4 w-4" />
                  }
                </span>
              </div>
            </SidebarMenuButton>
            {isCriminalDropdownOpen && (
              <div className="ml-8 mt-1 space-y-1">
                <Link 
                  key={'cmUploadReport'} 
                  to={`/criminal-management/upload-report`} 
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground hover:bg-sidebar-accent px-2 py-1 rounded-md"
                >
                  <Upload className="h-3 w-3" />
                  Upload Report
                </Link>
                <Link 
                  key={'cmReports'} 
                  to={`/criminal-management/reports`} 
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground hover:bg-sidebar-accent px-2 py-1 rounded-md"
                >
                  <Upload className="h-3 w-3" />
                  Reports
                </Link>
              </div>
              
            )}
          </SidebarMenuItem>
        </SidebarMenu>

        {/* Reports Dropdown - Only for ADMIN and SUPERADMIN */}
        {adminShoCoSdmDmAccess && (
          <SidebarMenu className="mt-2">
            <SidebarMenuItem>
              <SidebarMenuButton 
                asChild 
                onClick={() => setIsReportsDropdownOpen(!isReportsDropdownOpen)} 
                className="cursor-pointer flex items-center justify-between"
              >
                <div className="flex items-center w-full">
                  <IconChartBar className="mr-2 h-4 w-4" />
                  <span>Reports</span>
                  <span className="ml-auto">
                    {isReportsDropdownOpen ? 
                      <IconChevronDown className="h-4 w-4" /> : 
                      <IconChevronRight className="h-4 w-4" />
                    }
                  </span>
                </div>
              </SidebarMenuButton>
              {isReportsDropdownOpen && (
                <div className="ml-8 mt-1 space-y-1">
                  {data.reports.filter((report:any) => report.hasAccess).map((report, idx) => (
                    <Link 
                      key={idx} 
                      to={report.url} 
                      className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground hover:bg-sidebar-accent px-2 py-1 rounded-md"
                    >
                      <report.icon className="h-3 w-3" />
                      {report.name}
                    </Link>
                  ))}
                </div>
              )}
            </SidebarMenuItem>
          </SidebarMenu>
        )}

        <NavSecondary items={data.navSecondary} className="mt-auto" />
      </SidebarContent>
      <SidebarFooter>
        <NavUser user={data.user} />
      </SidebarFooter>
    </Sidebar>
  );
}